import {useLiveQuery} from 'dexie-react-hooks';
import type {
  InspectionResponse,
  InspectionSimpleResponse,
} from 'src/api/ProactApiClient';
import {offlineDb} from 'src/offline/db';
import {readSnapshotData} from 'src/offline/offlineQuery';
import type {OfflineSession, OutboxItem} from 'src/offline/types';
import {useAppSelector} from 'src/redux/hooks';
import {
  inspectionContextKey,
  inspectionSnapshotKeys,
  parseInspectionContextKey,
} from './snapshotKeys';

export interface InspectionOfflineDownloadState {
  session: OfflineSession | undefined;
  /** A downloaded, disconnect-ready session is bound to this inspection. */
  isDownloaded: boolean;
  /** A session exists but its download has not finished (resume/retry). */
  isDownloading: boolean;
}

export interface InspectionOfflineSessionState
  extends InspectionOfflineDownloadState {
  pendingCount: number;
  failedCount: number;
  syncedCount: number;
  outboxItems: OutboxItem[];
}

const useBoundSession = (
  inspectionId: number | undefined
): OfflineSession | undefined => {
  const userId = useAppSelector(state => state.session.user?.id);
  const instanceId = useAppSelector(state => state.session.instance?.id);
  return useLiveQuery(async () => {
    if (inspectionId === undefined || userId == null || instanceId == null) {
      return undefined;
    }
    const session = await offlineDb.sessions
      .where('contextKey')
      .equals(inspectionContextKey(inspectionId))
      .filter(current => current.status === 'active')
      .first();
    if (!session) return undefined;
    return session.userId === userId && session.instanceId === instanceId
      ? session
      : undefined;
  }, [inspectionId, userId, instanceId]);
};

/**
 * Sessions-only subscription for consumers that just need download state — list
 * cards pay a single live query and don't re-render on every outbox write.
 */
export const useInspectionOfflineDownload = (
  inspectionId: number | undefined
): InspectionOfflineDownloadState => {
  const session = useBoundSession(inspectionId);
  const ready = session?.ready ?? false;
  return {
    session,
    isDownloaded: ready,
    isDownloading: !!session && !ready,
  };
};

/** Boolean-only convenience over {@link useInspectionOfflineDownload}. */
export const useIsInspectionDownloaded = (
  inspectionId: number | undefined
): boolean => useInspectionOfflineDownload(inspectionId).isDownloaded;

export const useInspectionOfflineSession = (
  inspectionId: number | undefined
): InspectionOfflineSessionState => {
  const download = useInspectionOfflineDownload(inspectionId);
  const {session} = download;

  const outboxItems =
    useLiveQuery(async () => {
      if (!session) return [];
      return offlineDb.outbox.where('sessionId').equals(session.id).sortBy('seq');
    }, [session?.id]) ?? [];

  return {
    ...download,
    pendingCount: outboxItems.filter(item => item.status === 'pending').length,
    failedCount: outboxItems.filter(item => item.status === 'failed').length,
    syncedCount: outboxItems.filter(item => item.status === 'succeeded').length,
    outboxItems,
  };
};

// The meta snapshot holds the full InspectionResponse; the list card only needs
// the simple shape, whose terminal count is the non-remaining scoped devices.
const toSimpleInspection = (
  meta: InspectionResponse
): InspectionSimpleResponse => ({
  id: meta.id,
  siteId: meta.siteId,
  siteName: meta.siteName,
  siteNumber: meta.siteNumber,
  clientId: meta.clientId,
  clientName: meta.clientName,
  inspectionTypeId: meta.inspectionTypeId,
  inspectionTypeName: meta.inspectionTypeName,
  state: meta.state,
  assigneeId: meta.assigneeId,
  assigneeName: meta.assigneeName,
  scheduledStartAt: meta.scheduledStartAt,
  dueAt: meta.dueAt,
  completedAt: meta.completedAt,
  createdAt: meta.createdAt,
  scopedDeviceCount: meta.scopedDeviceCount,
  terminalDeviceCount: Math.max(meta.scopedDeviceCount - meta.remainingCount, 0),
  inspectionScheduleId: meta.inspectionScheduleId,
  inspectionScheduleCadence: meta.inspectionScheduleCadence,
  inspectionScheduleIntervalDays: meta.inspectionScheduleIntervalDays,
});

const dueTime = (inspection: InspectionSimpleResponse): number =>
  inspection.dueAt ? new Date(inspection.dueAt).getTime() : Number.MAX_SAFE_INTEGER;

/**
 * Every inspection downloaded to this device by the current user, read from the
 * meta snapshot of each ready session. `undefined` while the live query loads.
 */
export const useDownloadedInspections = ():
  | InspectionSimpleResponse[]
  | undefined => {
  const userId = useAppSelector(state => state.session.user?.id);
  const instanceId = useAppSelector(state => state.session.instance?.id);
  return useLiveQuery(async () => {
    if (userId == null || instanceId == null) return [];
    const sessions = await offlineDb.sessions
      .where('status')
      .equals('active')
      .filter(
        session =>
          session.ready &&
          session.userId === userId &&
          session.instanceId === instanceId &&
          parseInspectionContextKey(session.contextKey) !== undefined
      )
      .toArray();
    const metas = await Promise.all(
      sessions.map(session =>
        readSnapshotData<InspectionResponse>(
          session.id,
          inspectionSnapshotKeys.meta(
            parseInspectionContextKey(session.contextKey) as number
          )
        )
      )
    );
    return metas
      .filter((meta): meta is InspectionResponse => meta !== undefined)
      .map(toSimpleInspection)
      .sort((a, b) => dueTime(a) - dueTime(b));
  }, [userId, instanceId]);
};
